import React, { useState } from 'react';
import { ArrowLeft, Droplets, Circle, Gauge, Waves, AlertTriangle, HelpCircle, Download, Smartphone, ChevronRight, Search, Zap, Calculator } from 'lucide-react';
import CaudalTrapecialCalculator from './CaudalTrapecialCalculator';
import CaudalCircularCalculator from './CaudalCircularCalculator';
import FlujoNormalCircularCalculator from './FlujoNormalCircularCalculator';
import FlujoNormalTrapecialCalculator from './FlujoNormalTrapecialCalculator';
import FlujoCriticoTrapecialCalculator from './FlujoCriticoTrapecialCalculator';
import FlujoCriticoCircularCalculator from './FlujoCriticoCircularCalculator';
import CaudalCircularVelocidadCalculator from './CaudalCircularVelocidadCalculator';
import FlujoGradualmenteVariadoCalculator from './FlujoGradualmenteVariadoCalculator';
import SaltoHidraulicoCalculator from './SaltoHidraulicoCalculator';

interface OpenCanalesProps {
  onBack: () => void;
}

interface CanalTool {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  color: string;
  gradient: string;
}

const OpenCanales: React.FC<OpenCanalesProps> = ({ onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [activeTool, setActiveTool] = useState<string | null>(null);

  const canalTools: CanalTool[] = [
  {
    id: 'caudal-trap',
    title: 'Caudal Trapecial, Rectangular y Triangular',
    description: 'Calcula el caudal que pasa sobre un canal de riego para secciones trapeciales, rectangulares y triangulares.',
    icon: <Droplets className="w-7 h-7" />,
    category: 'Caudal',
    color: 'text-blue-600',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'caudal-circ',
    title: 'Caudal Circular',
    description: 'Calcula el caudal que pasa sobre un canal de riego con sección circular.',
    icon: <Circle className="w-7 h-7" />,
    category: 'Caudal',
    color: 'text-blue-600',
    gradient: 'from-blue-600 to-indigo-500'
  },
  {
    id: 'caudal-circ-vel',
    title: 'Caudal Circular con Velocidad',
    description: 'Calcula el caudal que pasa sobre un canal de riego usando datos de velocidad derivado de molinete.',
    icon: <Gauge className="w-7 h-7" />,
    category: 'Caudal',
    color: 'text-violet-600',
    gradient: 'from-violet-500 to-purple-500'
  },
  {
    id: 'flujo-normal-trap',
    title: 'Flujo Normal Trapecial, Rectangular y Triangular',
    description: 'Calcula el Tirante Normal, Velocidad, Froude, y otros parámetros hidráulicos en canales abiertos.',
    icon: <Waves className="w-7 h-7" />,
    category: 'Flujo Normal',
    color: 'text-teal-600',
    gradient: 'from-teal-500 to-emerald-500'
  },
  {
    id: 'flujo-normal-circ',
    title: 'Flujo Normal Circular',
    description: 'Calcula el Tirante Normal, Velocidad, Froude, y otros parámetros en canales de sección circular.',
    icon: <Waves className="w-7 h-7" />,
    category: 'Flujo Normal',
    color: 'text-teal-600',
    gradient: 'from-emerald-500 to-green-500'
  },
  {
    id: 'flujo-critico-trap',
    title: 'Flujo Crítico Trapecial, Rectangular y Triangular',
    description: 'Calcula el Tirante Crítico, Velocidad, Froude, y otros parámetros en condiciones de flujo crítico.',
    icon: <AlertTriangle className="w-7 h-7" />,
    category: 'Flujo Crítico',
    color: 'text-amber-600',
    gradient: 'from-amber-500 to-orange-500'
  },
  {
    id: 'flujo-critico-circ',
    title: 'Flujo Crítico Circular',
    description: 'Calcula el Tirante Crítico, Velocidad, Froude, y otros parámetros en canales circulares.',
    icon: <AlertTriangle className="w-7 h-7" />,
    category: 'Flujo Crítico',
    color: 'text-amber-600',
    gradient: 'from-orange-500 to-red-400'
  },
  {
    id: 'flujo-gv',
    title: 'Flujo Gradualmente Variado Trapecial, Rectangular y Triangular',
    description: 'Calcular el Perfil y tirante normal y crítico en canales de riego con flujo gradualmente variado.',
    icon: <Zap className="w-7 h-7" />,
    category: 'Flujo Variado',
    color: 'text-rose-600',
    gradient: 'from-rose-500 to-pink-500'
  },
  {
    id: 'salto-hidraulico',
    title: 'Salto Hidráulico Trapecial, Rectangular y Triangular',
    description: 'Calcula los conjugados y momentos menores y mayores, tirantes críticos, longitud de saltos, y más.',
    icon: <Zap className="w-7 h-7" />,
    category: 'Salto Hidráulico',
    color: 'text-fuchsia-600',
    gradient: 'from-fuchsia-500 to-purple-600'
  },
  {
    id: 'ayuda',
    title: 'Ayuda / Help',
    description: '¡Tienes dudas con los conceptos! ¡Resuélvelo! Accede a guías, definiciones y ejemplos prácticos.',
    icon: <HelpCircle className="w-7 h-7" />,
    category: 'Ayuda',
    color: 'text-sky-600',
    gradient: 'from-sky-500 to-blue-500'
  }];


  const categories = ['Todos', ...Array.from(new Set(canalTools.map((t) => t.category)))];

  const filtered = canalTools.filter((tool) => {
    const matchSearch = tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = activeFilter === 'Todos' || tool.category === activeFilter;
    return matchSearch && matchCat;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          {/* Water wave pattern */}
          <svg className="absolute bottom-0 left-0 w-full opacity-10" viewBox="0 0 1440 120" fill="none">
            <path d="M0,64 C360,120 720,0 1080,64 C1260,96 1380,80 1440,64 L1440,120 L0,120 Z" fill="white" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-200 hover:text-white mb-8 transition-colors group">

            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Volver a Herramientas HH</span>
          </button>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              





              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                openCanalesWeb
              </h1>
              <p className="text-lg text-blue-100 leading-relaxed">
                Le permite estimar parámetros hidráulicos en canales de riego de manera rápida y efectiva. También puede descargar nuestra aplicación para celular <strong className="text-cyan-300">"HFCanales"</strong>.
              </p>
            </div>

            {/* App download card */}
            














          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar calculadora..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />

          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 w-full md:w-auto">
            {categories.map((cat) =>
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeFilter === cat ?
              'bg-blue-600 text-white shadow-md' :
              'bg-gray-100 text-gray-600 hover:bg-gray-200'}`
              }>

                {cat}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Subtitle */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Seleccione el cálculo que desea realizar</h2>
        <p className="text-gray-500 mt-1">{filtered.length} herramienta{filtered.length !== 1 ? 's' : ''} disponible{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Tools Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((tool, index) =>
          <div
            key={tool.id}
            onClick={() => setActiveTool(tool.id)}
            className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
            style={{ animationDelay: `${index * 60}ms` }}>

              {/* Gradient top bar */}
              <div className={`h-1.5 bg-gradient-to-r ${tool.gradient}`}></div>

              <div className="p-6">
                {/* Icon & Category */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.gradient} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {tool.icon}
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 ${tool.color}`}>
                    {tool.category}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors leading-snug">
                  {tool.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                  {tool.description}
                </p>

                {/* Action */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-400 font-medium">Calculadora</span>
                  </div>
                  <div className="flex items-center gap-1 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all">
                    <span>Abrir</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* Hover glow effect */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 bg-gradient-to-br ${tool.gradient} transition-opacity duration-300 pointer-events-none rounded-2xl`}></div>
            </div>
          )}
        </div>

        {/* No results */}
        {filtered.length === 0 &&
        <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-500 font-medium">No se encontraron calculadoras</p>
            <p className="text-gray-400 mt-1">Intenta con otro término de búsqueda</p>
          </div>
        }
      </div>

      {/* Calculator Modals */}
      {activeTool === 'caudal-trap' &&
      <CaudalTrapecialCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'caudal-circ' &&
      <CaudalCircularCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'flujo-normal-circ' &&
      <FlujoNormalCircularCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'flujo-normal-trap' &&
      <FlujoNormalTrapecialCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'flujo-critico-trap' &&
      <FlujoCriticoTrapecialCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'flujo-critico-circ' &&
      <FlujoCriticoCircularCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'caudal-circ-vel' &&
      <CaudalCircularVelocidadCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'flujo-gv' &&
      <FlujoGradualmenteVariadoCalculator onClose={() => setActiveTool(null)} />
      }
      {activeTool === 'salto-hidraulico' &&
      <SaltoHidraulicoCalculator onClose={() => setActiveTool(null)} />
      }

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <p className="text-gray-400 text-sm">© 2025 OpenHH — openCanalesWeb | Herramientas de hidráulica de canales</p>
        </div>
      </div>
    </div>);

};

export default OpenCanales;